import { DEFAULT_LANGUAGE, SUPPORTED_LOCALES } from '@/shared/localization';
import type { Metadata } from 'next';

type LocaleParam = {
  locale?: string;
};

type BlogArticleParam = LocaleParam & {
  slug: string;
};

// 페이지 generateMetadata 의 alternates(self-canonical + ko/en/x-default hreflang)를
// 단일 출처로 생성한다. build(locale) 는 그 라우트의 로케일별 경로를 돌려준다.
// 반환 경로는 상대값이며, root layout 의 metadataBase 가 절대 URL 로 해석한다.
// → 두 언어판이 서로의 hreflang 대체 버전으로 묶여 GSC 가 중복/리디렉션 오류로 보지 않는다.
export function localeAlternates(
  locale: string,
  build: (locale: string) => string,
): NonNullable<Metadata['alternates']> {
  const languages: Record<string, string> = {};
  for (const supported of SUPPORTED_LOCALES) {
    languages[supported] = build(supported);
  }
  languages['x-default'] = build(DEFAULT_LANGUAGE);

  return {
    canonical: build(locale),
    languages,
  };
}

const withLocale = (path: string, locale?: string) => {
  if (!locale) return path;
  if (path === `/${locale}` || path.startsWith(`/${locale}/`)) {
    return path;
  }
  return `/${locale}${path}`;
};

export const publicRoutes = {
  home: ({ locale }: LocaleParam = {}) => (locale ? `/${locale}` : '/'),
  pricing: ({ locale }: LocaleParam = {}) => withLocale('/pricing', locale),
  blog: ({ locale }: LocaleParam = {}) => withLocale('/blog', locale),
  blogArticle: ({ locale, slug }: BlogArticleParam) =>
    `${publicRoutes.blog({ locale })}/${slug}`,
};

export function localizePath(path: string, locale?: string) {
  if (!path.startsWith('/')) return path;
  return withLocale(path, locale);
}

export function resolvePublicHref(
  locale: string,
  raw: string | undefined,
): string | undefined {
  if (raw === undefined) return undefined;
  if (raw.startsWith('mailto:') || raw.startsWith('http')) return raw;

  if (raw === '' || raw === 'intro') {
    return publicRoutes.home({ locale });
  }

  if (raw === 'pricing' || raw === 'intro/pricing') {
    return publicRoutes.pricing({ locale });
  }

  if (raw === 'blog' || raw === 'intro/blog') {
    return publicRoutes.blog({ locale });
  }

  if (raw.startsWith('blog/')) {
    return publicRoutes.blogArticle({
      locale,
      slug: raw.slice('blog/'.length),
    });
  }

  if (raw.startsWith('/')) {
    return localizePath(raw, locale);
  }

  return localizePath(`/${raw}`, locale);
}
